import { updateKeyInStore } from 'App';
import { store } from 'app/redux/store';
import { countries } from 'countries';
import * as Yup from 'yup';
import {
  AccountsListStateValues,
  TAccountsRow,
  TAccountsTable,
  updateFieldArrayKey,
  updateKey,
} from './slice';

// predicate:
// 'Displayed in case the user selects Yes to the "At any point during Year, was the sum across all your non-US financial accounts over USD 10,000?" question',

export const fields: IBaseField<
  keyof AccountsListStateValues | keyof TAccountsRow
>[] = [
  {
    name: 'accountsTable',
    label:
      'Please enter all your non-US financial accounts in the table below. Please include highest balance for each account.',
    type: 'fieldArray',
    tooltip: '',
    comment: true,
    effect: (payload) => store.dispatch(updateFieldArrayKey(payload)),
    defaultDependency: {
      accountType: null,
      whoIsThePrimaryAccountOwner: null,
      yourName: null,
      doYouOwnThisAccountByYourselfOrIsItAJointAccount: null,
      whoIsTheJointOwner: null,
      spouseName: null,
      doYouHaveSignatoryAuthorityjoinOwnership: null,
      theirName: null,
      whatIsYourRelationshipToTheAccountOwner: null,
      theirSsifAvailable: null,
      theirAddress: null,
      accountNicknameoptional: null,
      nameOfFinancialInstitutionInWhichAccountIsHeld: null,
      accountNumber: null,
      highestBalanceDuringTheYear: null,
      addressOfFinancialInstitutionInWhichTheAccountIsHeldstreetAddress: null,
      city: null,
      stateOrProvincemexicoAndCanadaOnly: null,
      postCode: null,
      country: null,
      wasTheAccountOpenedDuringTheTaxYear: null,
      wasTheAccountClosedDuringYear: null,
    },
    dependencies: [
      {
        name: 'accountType',
        label: 'Account Type',
        type: 'select',
        options: [
          {
            label: 'Bank Account',
            value: 'Bank Account',
          },
          {
            label: 'Investment Account',
            value: 'Investment Account',
          },
          {
            label: 'Business Account',
            value: 'Business Account',
          },
          {
            label: 'Insurance/Annuity',
            value: 'Insurance/Annuity',
          },
          {
            label: 'Retirement Plan',
            value: 'Retirement Plan',
          },
          {
            label: 'Non-US Trust in which you are a beneficiary',
            value: 'Non-US Trust in which you are a beneficiary',
          },
          {
            label: 'Escrow',
            value: 'Escrow',
          },
        ],
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
      },
      {
        name: 'whoIsThePrimaryAccountOwner',
        label: 'Who is the primary account owner?',
        type: 'select',
        options: [
          {
            label: 'Myself',
            value: 'Myself',
          },
          {
            label: 'Spouse',
            value: 'Spouse',
          },
          {
            label: 'Someone else',
            value: 'Someone else',
          },
        ],
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),

        dependencies: [
          {
            name: 'yourName',
            label: 'Your Name',
            type: 'text',
            predicate: (value) => value === 'Myself',
            tooltip: '',
            comment: true,
            effect: updateKeyInStore(updateKey),
          },
          {
            name: 'doYouOwnThisAccountByYourselfOrIsItAJointAccount',
            label:
              'Do you own this account by yourself or is it a joint account?',
            type: 'select',
            options: [
              {
                label: 'I am the sole owner of the account',
                value: 'I am the sole owner of the account',
              },
              {
                label: 'Joint Ownership',
                value: 'Joint Ownership',
              },
            ],
            predicate: (value) => value === 'Myself',
            tooltip: '',
            comment: true,
            effect: updateKeyInStore(updateKey),

            dependencies: [
              {
                name: 'whoIsTheJointOwner',
                label: 'Who is the joint owner?',
                type: 'select',
                options: [
                  {
                    label: 'Spouse',
                    value: 'Spouse',
                  },
                  {
                    label: 'Someone else',
                    value: 'Someone else',
                  },
                ],
                predicate: (value) => value === 'Joint Ownership',
                tooltip: '',
                comment: true,
                effect: updateKeyInStore(updateKey),

                dependencies: [
                  {
                    name: 'spouseName',
                    label: 'Spouse Name',
                    type: 'text',
                    predicate: (value) => value === 'Spouse',
                    tooltip: '',
                    comment: true,
                    effect: updateKeyInStore(updateKey),
                  },
                  {
                    name: 'theirName',
                    label: 'Their Name',
                    type: 'text',
                    predicate: (value) => value === 'Someone else',
                    tooltip: '',
                    comment: true,
                    effect: updateKeyInStore(updateKey),
                  },
                  {
                    name: 'whatIsYourRelationshipToTheAccountOwner',
                    label: 'What is your relationship to the account owner',
                    type: 'text',
                    predicate: (value) => value === 'Someone else',
                    tooltip: '',
                    comment: true,
                    effect: updateKeyInStore(updateKey),
                  },
                  {
                    name: 'theirSsifAvailable',
                    label: 'Their SS# (if available)',
                    type: 'number',
                    predicate: (value) => value === 'Someone else',
                    tooltip: '',
                    comment: true,
                    effect: updateKeyInStore(updateKey),
                  },
                  {
                    name: 'theirAddress',
                    label: 'Their Address',
                    type: 'text',
                    predicate: (value) => value === 'Someone else',
                    tooltip: '',
                    comment: true,
                    effect: updateKeyInStore(updateKey),
                  },
                ],
              },
            ],
          },
          {
            name: 'doYouHaveSignatoryAuthorityjoinOwnership',
            label: 'Do you have signatory authority/join ownership?',
            type: 'select',
            options: [
              {
                label: 'I, your Name, do not have control over the account',
                value: 'I, your Name, do not have control over the account',
              },
              {
                label: 'My spouse and I are joint owners',
                value: 'My spouse and I are joint owners',
              },
              {
                label: 'I, your Name, have signatory authority',
                value: 'I, your Name, have signatory authority',
              },
            ],
            predicate: (value) => value === 'Spouse',
            tooltip: '',
            comment: true,
            effect: updateKeyInStore(updateKey),
          },
        ],
      },
      {
        name: 'accountNicknameoptional',
        label: 'Account Nickname (optional)',
        type: 'text',
        effect: updateKeyInStore(updateKey),
      },
      {
        name: 'nameOfFinancialInstitutionInWhichAccountIsHeld',
        label:
          'Name of Financial Institution in which account is held Ie Bank Name',
        type: 'text',
        effect: updateKeyInStore(updateKey),
      },
      {
        name: 'accountNumber',
        label: 'Account Number',
        type: 'text',
        effect: updateKeyInStore(updateKey),
      },
      {
        name: 'highestBalanceDuringTheYear',
        label: 'Highest Balance during the year',
        type: 'currency',
        effect: updateKeyInStore(updateKey),
      },
      {
        name: 'addressOfFinancialInstitutionInWhichTheAccountIsHeldstreetAddress',
        label:
          'Address of Financial Institution in which the account is held (Street Address)',
        type: 'text',
        effect: updateKeyInStore(updateKey),
      },
      {
        name: 'city',
        label: 'City',
        type: 'text',
        effect: updateKeyInStore(updateKey),
      },
      {
        name: 'stateOrProvincemexicoAndCanadaOnly',
        label: 'State or Province (Mexico and Canada only)',
        type: 'text',
        effect: updateKeyInStore(updateKey),
      },
      {
        name: 'postCode',
        label: 'Post Code',
        type: 'text',
        effect: updateKeyInStore(updateKey),
      },
      {
        name: 'country',
        label: 'Country',
        type: 'country',
        placeholder: 'Select Country',
        effect: updateKeyInStore(updateKey),
      },
      {
        name: 'wasTheAccountOpenedDuringTheTaxYear',
        label: 'Was the account opened during the tax year?',
        type: 'radio',
        effect: updateKeyInStore(updateKey),
      },
      {
        name: 'wasTheAccountClosedDuringYear',
        label: 'Was the account closed during Year?',
        type: 'radio',
        effect: updateKeyInStore(updateKey),
      },
    ],
  },
];

export const validationSchema = Yup.object().shape({});
